import type {
  StoreNotification,
  StoreNotificationSummary,
} from "@/types/storeNotifications";
import { apiUrl } from "@/utils/url";
import { HttpClient } from "../HttpClient";
import { isAbortError, toServiceError } from "../errors";
import type { IStoreNotificationsService } from "./IStoreNotificationsService";

/** The API omits `latest` when there is none, rather than sending null. */
type UnseenSummaryResponse = {
  unseenCount: number;
  latest?: StoreNotification | null;
};

export class StoreNotificationsService implements IStoreNotificationsService {
  private readonly httpClient = new HttpClient({ baseURL: apiUrl });

  async getUnseenSummary(
    storeId: number,
    signal?: AbortSignal,
  ): Promise<StoreNotificationSummary> {
    const { unseenCount, latest } =
      await this.httpClient.get<UnseenSummaryResponse>(
        `/store/${storeId}/notifications/unseen/summary`,
        { signal },
      );

    return { unseenCount, latest: latest ?? null };
  }

  async getUnseen(
    storeId: number,
    signal?: AbortSignal,
  ): Promise<StoreNotification[]> {
    try {
      return await this.httpClient.get<StoreNotification[]>(
        `/store/${storeId}/notifications/unseen`,
        { signal },
      );
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw toServiceError(
        error,
        "Não foi possível carregar as notificações. Atualize a página e tente novamente.",
      );
    }
  }

  async markOrderSeen(
    storeId: number,
    shopifyOrderId: number,
    signal?: AbortSignal,
  ): Promise<void> {
    try {
      const notifications = await this.httpClient.get<StoreNotification[]>(
        `/store/${storeId}/notifications/shopify-order/${shopifyOrderId}`,
        { signal },
      );
      await Promise.all(
        notifications
          .filter(({ seenOn }) => !seenOn)
          .map(({ id }) =>
            this.httpClient.patch<void>(
              `/store/${storeId}/notifications/${id}/seen`,
              undefined,
              { signal },
            ),
          ),
      );
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw toServiceError(
        error,
        "Não foi possível marcar a notificação da encomenda como vista.",
      );
    }
  }

  async markAllSeen(storeId: number, signal?: AbortSignal): Promise<void> {
    try {
      await this.httpClient.patch<void>(
        `/store/${storeId}/notifications/seen-all`,
        undefined,
        { signal },
      );
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw toServiceError(
        error,
        "Não foi possível marcar as notificações como vistas. Tente novamente.",
      );
    }
  }
}
